import React, { useCallback, useMemo, useState } from 'react'

import { HiMiniCheck } from 'react-icons/hi2'
import { LuSlidersHorizontal, LuX } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { MiniBadge } from '@/components/common/Badge'
import Button from '@/components/common/Button'
import IconButton from '@/components/common/IconButtonV2'
import PopoverV2 from '@/components/common/PopoverV2'

export interface FilterOption {
  label: string
  value: string
}

export interface FilterGroup {
  id: string
  label: string
  options: FilterOption[]
  value: string[]
  defaultValue?: string[]
  selectionMode?: 'multiple' | 'single'
  isOptionChecked?: (optionValue: string, value: string[]) => boolean
  onCheckedChange: (value: string, checked: boolean) => void
}

interface DataTableFilterMenuProps {
  groups: FilterGroup[]
  className?: string
}

const onOpenAutoFocus = (event: Event) => {
  event.preventDefault()
}

/**
 * A filter menu that displays multiple filter groups in a clean popover.
 * Each group appears as a column with options that can be toggled.
 */
export const DataTableFilterMenu: React.FC<DataTableFilterMenuProps> = ({
  groups,
  className,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Calculate total active filter count across all groups
  const totalActiveFilters = useMemo(
    () =>
      groups.reduce((acc, group) => {
        const defaultValue = new Set(group.defaultValue ?? [])
        const activeCount = group.value.filter(
          (value) => !defaultValue.has(value),
        ).length
        return acc + activeCount
      }, 0),
    [groups],
  )

  return (
    <PopoverV2 open={isOpen} onOpenChange={setIsOpen}>
      <PopoverV2.Trigger asChild>
        <Button
          variant="outline-compact"
          className={twMerge('active:scale-100 gap-1.5', className)}
        >
          <LuSlidersHorizontal size={14} />
          <span>Filters</span>
          {totalActiveFilters > 0 && (
            <MiniBadge
              count={totalActiveFilters}
              variant="soft"
              className="ml-0.5 text-[9px]"
            />
          )}
        </Button>
      </PopoverV2.Trigger>
      <PopoverV2.Content
        align="start"
        className="relative w-auto p-0 shadow-lg"
        sideOffset={4}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <IconButton
          type="button"
          aria-label="Close filters"
          tooltip="Close filters"
          onClick={handleClose}
          className="absolute right-1.5 top-1.5 z-10"
        >
          <LuX size={14} />
        </IconButton>
        <div className="flex">
          {groups.map((group, idx) => (
            <FilterGroupColumn
              key={group.id}
              group={group}
              isFirst={idx === 0}
              isLast={idx === groups.length - 1}
            />
          ))}
        </div>
      </PopoverV2.Content>
    </PopoverV2>
  )
}

interface FilterGroupColumnProps {
  group: FilterGroup
  isFirst: boolean
  isLast: boolean
}

const FilterGroupColumn: React.FC<FilterGroupColumnProps> = ({
  group,
  isFirst,
  isLast,
}) => {
  const handleToggle = useCallback(
    (optionValue: string) => () => {
      if (group.selectionMode === 'single') {
        group.onCheckedChange(optionValue, true)
        return
      }
      const isChecked =
        group.isOptionChecked?.(optionValue, group.value) ??
        group.value.includes(optionValue)
      group.onCheckedChange(optionValue, !isChecked)
    },
    [group],
  )

  return (
    <div
      className={twMerge(
        'flex flex-col min-w-[190px]',
        !isLast && 'border-r border-primary-100 dark:border-primary-800',
        'dark:bg-default-dark',
        'divide-y divide-primary-100 dark:divide-primary-800',
      )}
    >
      {/* Column header */}
      <div
        className={twMerge(
          'px-3 py-2 bg-primary-50 dark:bg-primary-900',
          isFirst && 'rounded-tl-md',
          isLast && 'rounded-tr-md',
        )}
      >
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
          {group.label}
        </span>
      </div>

      {/* Options */}
      <div className="py-1">
        {group.options.map((option) => {
          const isChecked =
            group.isOptionChecked?.(option.value, group.value) ??
            group.value.includes(option.value)

          return (
            <button
              key={option.value}
              type="button"
              onClick={handleToggle(option.value)}
              className={twMerge(
                'flex items-center justify-between gap-3 w-full px-3 py-1.5 text-left text-sm transition-colors',
                'focus:outline-none focus:bg-primary-50 dark:focus:bg-primary-800/50',
                'hover:!bg-primary-100 dark:hover:!bg-primary-800',
                isChecked
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-primary-700 dark:text-primary-300',
              )}
            >
              <span>{option.label}</span>
              {isChecked && (
                <HiMiniCheck
                  size={16}
                  className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
